/* Presentation view */
import React, { lazy } from "react";
import { ModalRoute, LazyModalRoute, Modal } from "seed/helpers";
import l from "components/util/l";
import StyleColor from "components/util/helpers/StyleColor"
import Collaboration from "components/presentation/forms/collaboration/Collaboration";
import { HasPermission } from "components/util/helpers/HasPermission";
import { PERMISSIONS } from "components/util/permissions";
import { HOST_MODAL_PATHS } from "components/util/host_routes";
import Start from "components/presentation/start/Start"
import Assistant from "components/presentation/assistant/Assistant";
import CopyClipboardButton from "components/util/helpers/CopyClipboardButton";
import PresentationHeader from "./Presentation.sub.Header.view";
import PresentationSection from "./Presentation.sub.Section.view";
import PresentationTemplateForm from "./Presentation.sub.TemplateForm.view";
import DevExtension from "components/util/helpers/DevExtension";
import FormAction from "components/tmisc/apps/FormAction";
import Portal from "components/tmisc/portal/Portal";
import { AnimatedContainer } from "components/presentation/main/layout/SolutionBackground";
import DevLogs from "components/util/helpers/DevLogs";
import "styles/css/presentation/Presentation.css";

const FlowFormEdit = lazy(() => import("components/presentation/forms/flows/FlowFormEdit"));
const SyncFlow = lazy(() => import("components/presentation/sync/SyncFlow"));
const FolderFormCreate = lazy(() => import("components/presentation/forms/folders/FolderFormCreate"));
const FolderFormEdit = lazy(() => import("components/presentation/forms/folders/FolderFormEdit"));


const PresentationView = ({
  user,
  flow,
  buildStatus,
  cacheViews,
  showManageCollections,
  showTopnav,
  presentationSettings,
  activeSection,
  userRole,
  isAdmin,
  isDeveloper,
  isEmbedded,
  isPinnedToFlow,
  isStartShown,
  isContactFormShown,
  isAssistantShown,
  refreshFlow,
  refreshChat,
  closeStart,
  setCacheViews,
  onClickStartButton,
  onClickTab,
  onChangeFlow,
  onClickPinView,
  onClickHideAssistant,
  onClickHideContactForm,
  onClickShowAssistant,
  onClickShowContactUs,
  onSubmitForm
}) => (
  <div>
    <StyleColor theme={flow.theme} backgroundHeight="0vh" />

    <div id="presentation"
      className={(!showTopnav ? "no-topnav " : "")
        + `section-${activeSection.name} `}>
      {showTopnav &&
        <PresentationHeader
          user={user}
          flow={flow}
          buildStatus={buildStatus}
          presentationSettings={presentationSettings}
          activeSection={activeSection}
          isAdmin={isAdmin}
          isDeveloper={isDeveloper}
          isEmbedded={isEmbedded}
          isPinnedToFlow={isPinnedToFlow}
          onClickStartButton={onClickStartButton}
          onClickTab={onClickTab}
          onClickPinView={onClickPinView}
          onClickShowAssistant={onClickShowAssistant}
          onClickShowContactUs={onClickShowContactUs}
        />
      }

      {isStartShown &&
        <div id="presentation-start"
          className="animate__animated animate__fadeInLeft animate__faster">
          <Start
            user={user}
            currentFlow={flow}
            closeStart={closeStart}
            onChangeFlow={onChangeFlow} />
        </div>}

      {
        // Show chat if built, in prototype mode, template mode, or if user is developer. 
        // Otherwise show client portal.
        (buildStatus == "BUILT" || buildStatus == "TEMPLATE_STARTED" || isDeveloper) ?
          <div
            className="position-relative h-100 flex-fill">
            <div className={"h-100 m-auto "}>
              <div id="presentation-content">
                {
                  function () {
                    const activeViewSignature = [
                      activeSection.name,
                      activeSection.redirectType,
                      activeSection.redirectTarget,
                      activeSection.redirectReceivedAt,
                      showTopnav,
                    ].join("|");

                    // Cache views to avoid reloading components when switching tabs
                    const cachedView = cacheViews.find(view => view.name == activeSection.name)
                    if (!cachedView || cachedView.signature != activeViewSignature) {
                      const views = presentationSettings?.views ?? []
                      const component = <PresentationSection
                        flow={flow}
                        cacheViews={cacheViews}
                        userRole={userRole}
                        isAdmin={isAdmin}
                        isDeveloper={isDeveloper}
                        showManageCollections={showManageCollections}
                        showTopnav={showTopnav}
                        refreshFlow={refreshFlow}
                        refreshChat={refreshChat}
                        setCacheViews={setCacheViews}
                        onClickTab={onClickTab}
                        views={views}
                        currentView={activeSection}
                      />

                      const nextCachedView = {
                        name: activeSection.name,
                        signature: activeViewSignature,
                        component: component,
                      };

                      if (cachedView) {
                        setCacheViews(cacheViews.map(view =>
                          view.name == activeSection.name ? nextCachedView : view
                        ))
                      }
                      else {
                        // Add to cache
                        setCacheViews([...cacheViews, nextCachedView])
                      }
                    }

                    return cacheViews.map(view => {
                      return <div
                        key={view.name}
                        className={`w-100 h-100 view-container view-${view.name} `
                          + (activeSection.name == view.name ? "active " : " ")}>
                        {view.component}
                      </div>
                    })
                  }()
                }

              </div>
            </div>
          </div>
          : <>
            <AnimatedContainer flow={flow} showTopnav={showTopnav}>
              <Portal
                flow={flow}
                buildStatus={buildStatus}
              />
            </AnimatedContainer>
          </>
      }

    </div>

    {isAssistantShown &&
      <Assistant flow={flow} onClose={onClickHideAssistant} />}

    {
      isContactFormShown &&
      <Modal
        width={600}
        height={500}
        onClose={onClickHideContactForm}
        onSubmitForm={onSubmitForm}
        component={PresentationTemplateForm}
      />
    }

    {/* Share modal */}
    <HasPermission permission={PERMISSIONS.SHOW_SHARE} defaultValue={true} fallback={null}>
      <LazyModalRoute
        width={600}
        height={600}
        animation={""}
        path={HOST_MODAL_PATHS.share}
        component={({ match }) => <Collaboration flowId={match.params.flowId || flow.id} onlyAdminRoles={false} />}
      />
    </HasPermission>

    {/* Edit flow modal */}
    <LazyModalRoute
      width={530}
      height={500}
      path={HOST_MODAL_PATHS.editFlow}
      component={({ match }) =>
        <FlowFormEdit
          match={{ params: { "flowId": match.params.flowId || flow.id } }}
        />
      }
    />

    {/* Sync modal: a new solution from Workix */}
    <LazyModalRoute
      width={520}
      height={640}
      path={HOST_MODAL_PATHS.syncFlow}
      component={SyncFlow}
    />

    {/* Folder modals */}
    <LazyModalRoute
      width="470"
      height="450"
      path={HOST_MODAL_PATHS.createFolder}
      component={FolderFormCreate}
    />

    <LazyModalRoute
      width="470"
      height="450"
      path={HOST_MODAL_PATHS.editFolder}
      component={({ match }) => <FolderFormEdit match={match} />}
    />

    {/* Website modal */}
    <ModalRoute
      width={750}
      height={window.innerHeight - 150}
      animation={""}
      path={HOST_MODAL_PATHS.website}
      component={({ match }) => <div className="card w-100 h-100">
        <div className="w-100 text-center py-2">{l("Liga para compartir%PA105")}:&nbsp;
          <a href={decodeURIComponent(match.params?.url)} target="_blank" rel="noreferrer">{decodeURIComponent(match.params?.url)}</a>
          <CopyClipboardButton link={decodeURIComponent(match.params?.url)} />
        </div>
        <iframe className="w-100 h-100"
          src={decodeURIComponent(match.params?.url)}
          referrerpolicy="same-origin" title="website" />
      </div>}
    />

    <DevExtension />
    <DevLogs flow={flow} />

    <FormAction flow={flow} />

  </div>
)

export default PresentationView;
